import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DealerApiWrapper } from "./style/DealerApiWrapper";
import { DEALER_API_TABS, dealerApiAccess } from "./helpers";
import { checkForSuperAdmin } from "../../core/utils";
import RequestsTab from "./components/RequestsTab";
import KeysTab from "./components/KeysTab";
import PushesTab from "./components/PushesTab";
import ActivityTab from "./components/ActivityTab";
import ReferenceTab from "./components/ReferenceTab";
import DocsTab from "./components/DocsTab";
import IssueMasterKeyModal from "./components/IssueMasterKeyModal";
import QuotaModal from "./components/QuotaModal";

/**
 * Dealer API — dealerships push their stock from their DMS and suppliers read it back with keys.
 * Admin side (feature `dealer_api`): review access requests, manage keys, watch pushes and the
 * pipeline, set daily quotas, read what dealers read. API: /api/admin/dealer-api
 * (ctb_lumino docs/api/admin/dealer-api.md). Runbook: ctb_lumino docs/dealer-api-runbook.md.
 */
const DealerApi = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const access = dealerApiAccess();
  const superAdmin = checkForSuperAdmin();

  const requestedTab = searchParams.get("tab");
  const activeTab = DEALER_API_TABS.some((t) => t.id === requestedTab) ? requestedTab : DEALER_API_TABS[0].id;
  // Shared by Push activity and Activity: "show me this dealership".
  const subsidiaryId = /^\d+$/.test(searchParams.get("subsidiary") || "") ? searchParams.get("subsidiary") : "";

  const [issuingFor, _issuingFor] = useState(null);
  const [quotasFor, _quotasFor] = useState(null);
  const [keysRefresh, _keysRefresh] = useState(0);
  const [activityRefresh, _activityRefresh] = useState(0);

  const navigate = (tab, subsidiary = subsidiaryId) => {
    const params = { tab };
    if (subsidiary) params.subsidiary = subsidiary;
    setSearchParams(params);
  };

  const shared = {
    access,
    superAdmin,
    onIssueKey: (subsidiary) => _issuingFor(subsidiary),
    onOpenQuotas: (subsidiary) => _quotasFor(subsidiary),
  };

  return (
    <DealerApiWrapper>
      <div className="auctionMainContainer">
        <div className="row mt-4">
          <div className="col-md-6 offset-md-3 mb-2">
            <h1>Dealer API</h1>
          </div>
        </div>

        <div className="tabBtn d-flex">
          {DEALER_API_TABS.map((tab) => (
            <button key={tab.id} className={activeTab === tab.id ? "activeTab" : ""} onClick={() => navigate(tab.id)}>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "requests" && <RequestsTab {...shared} onShowActivity={(id) => navigate("pushes", String(id))} />}
        {activeTab === "keys" && <KeysTab {...shared} refreshToken={keysRefresh} />}
        {activeTab === "pushes" && <PushesTab subsidiaryId={subsidiaryId} onSubsidiaryChange={(id) => navigate("pushes", id)} />}
        {activeTab === "activity" && (
          <ActivityTab
            {...shared}
            subsidiaryId={subsidiaryId}
            onSubsidiaryChange={(id) => navigate("activity", id)}
            onShowPushes={(id) => navigate("pushes", id)}
            refreshToken={activityRefresh}
          />
        )}
        {activeTab === "reference" && <ReferenceTab />}
        {activeTab === "docs" && <DocsTab />}
      </div>

      <IssueMasterKeyModal
        subsidiary={issuingFor}
        onClose={() => _issuingFor(null)}
        onIssued={() => _keysRefresh((n) => n + 1)}
      />
      <QuotaModal
        subsidiary={quotasFor}
        onClose={() => _quotasFor(null)}
        onSaved={() => _activityRefresh((n) => n + 1)}
      />
    </DealerApiWrapper>
  );
};

export default DealerApi;
